import React, { useState, useEffect } from "react";
import { Table, Input } from "antd";
import type { TableProps } from "antd";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getSubscriptions,
  deleteSubscription,
  Subscription,
} from "../../../api/subscription";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import { highlightMatch } from "../../../core/common/highlightmatch";

// Define interfaces for type safety
interface WidgetData {
  total_transaction: number;
  total_subscribers: number;
  active_subscribers: number;
  expired_subscribers: number;
}

const Subscriptionpayment: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "Subscriber",
    "Plan",
    "Payment Method",
    "Amount",
    "Created Date",
    "Expired On",
    "Status",
    "Actions",
  ]);
  const [widgetData, setWidgetData] = useState<WidgetData>({
    total_transaction: 0,
    total_subscribers: 0,
    active_subscribers: 0,
    expired_subscribers: 0,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortOption, setSortOption] = useState("recently_added");

  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: initialSettings.startDate,
    endDate: initialSettings.endDate,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptions();
      if (!Array.isArray(data)) throw new Error("Invalid response format");
      setSubscriptions(data);

      const total_transaction = data.reduce(
        (sum, sub) => sum + (sub.amount || 0),
        0
      );
      const total_subscribers = data.length;
      const active_subscribers = data.filter(
        (sub) => sub.status === "Active"
      ).length;
      const expired_subscribers = data.filter(
        (sub) => sub.status === "Expired"
      ).length;

      setWidgetData({
        total_transaction,
        total_subscribers,
        active_subscribers,
        expired_subscribers,
      });
    } catch (error: any) {
      console.error(error?.message || "Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriptionId: string) => {
    try {
      await deleteSubscription(subscriptionId);
      setSubscriptions((prev) =>
        prev.filter((sub) => sub.subscription_id !== subscriptionId)
      );
      toast.success("Subscription deleted successfully");
      fetchSubscriptions();
    } catch (error) {}
  };

  const handleView = (subscriptionId: string) => {
    console.log("Viewing subscription:", subscriptionId);
  };

  // Dynamic function to toggle column visibility
  const handleToggleColumn = (column: string): void => {
    setVisibleColumns((prev) =>
      prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column]
    );
  };

  const allColumns: TableProps<Subscription>["columns"] = [
    {
      title: "Subscriber",
      dataIndex: "client_name",
      key: "Subscriber",
      render: (client_name: string) =>
        highlightMatch(client_name || "N/A", searchTerm),
    },
    {
      title: "Plan",
      dataIndex: "plan_name",
      key: "Plan",
      render: (plan_name: string) =>
        highlightMatch(plan_name || "N/A", searchTerm),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_id",
      key: "Payment Method",
      render: (payment_id: string) =>
        highlightMatch(payment_id || "N/A", searchTerm),
    },
    {
      title: "Amount",
      key: "Amount",
      render: (_: any, record: Subscription) => `$${record.amount || 0}`,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "Created At",
      render: (date: string) =>
        new Date(date).toLocaleString("en-IN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Kolkata", // Adjust for your local timezone
        }),
    },
    {
      title: "Created Date",
      dataIndex: "subscription_start_date",
      key: "Created Date",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Expired On",
      dataIndex: "subscription_end_date",
      key: "Expired On",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "Status",
      render: (status: string) => (
        <span
          className={`badge ${
            status === "Active" ? "bg-success" : "bg-danger"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "Actions",
      render: (_: any, record: Subscription, index: number) => (
        <div className="dropdown table-action" key={index}>
          <button
            className="btn btn-sm btn-light"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            id={`dropdownMenuButton${index}`}
          >
            <i className="ti ti-dots-vertical" />
          </button>
          <ul
            className="dropdown-menu dropdown-menu-end"
            aria-labelledby={`dropdownMenuButton${index}`}
          >
            <li>
              <Link
                className="dropdown-item"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#view_invoice"
                onClick={() => handleView(record.subscription_id)}
              >
                <i className="ti ti-eye text-info me-2" />
                View
              </Link>
            </li>
            <li>
              <Link
                className="dropdown-item"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#delete_company"
                onClick={() => handleDelete(record.subscription_id)}
              >
                <i className="ti ti-trash-x text-danger me-2" />
                Delete
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  const columns = allColumns.filter((col) =>
    visibleColumns.includes(col.key as string)
  );

  const filteredData = subscriptions
    .filter(
      (sub) =>
        sub.client_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.payment_id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((sub) => {
      if (!dateRange.startDate || !dateRange.endDate) return true;
      const created = new Date(sub.created_at).getTime(); // Adjust if your API uses a different date field
      return (
        created >= dateRange.startDate.getTime() &&
        created <= dateRange.endDate.getTime()
      );
    });
  // Sort data based on the selected sort option
  if (sortOption === "asc") {
    filteredData.sort((a, b) =>
      (a.plan_name || "").localeCompare(b.plan_name || "")
    );
  } else if (sortOption === "desc") {
    filteredData.sort((a, b) =>
      (b.plan_name || "").localeCompare(a.plan_name || "")
    );
  } else if (sortOption === "recently_added") {
    filteredData.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title"><i className="bi bi-cart-check text-primary me-2"/>Subscription</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <Link
                      to="#"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      data-bs-original-title="Refresh"
                      onClick={fetchSubscriptions}
                    >
                      <i className="ti ti-refresh-dot" />
                    </Link>
                    <Link
                      to="#"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      data-bs-original-title="Collapse"
                      id="collapse-header"
                    >
                      <i className="ti ti-chevrons-up" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              {[
                {
                  title: "Total Transaction",
                  value: `₹${widgetData.total_transaction}`,
                  key: "total_transaction",
                },
                {
                  title: "Total Subscribers",
                  value: widgetData.total_subscribers,
                  key: "total_subscribers",
                },
                {
                  title: "Active Subscribers",
                  value: widgetData.active_subscribers,
                  key: "active_subscribers",
                },
                {
                  title: "Expired Subscribers",
                  value: widgetData.expired_subscribers,
                  key: "expired_subscribers",
                },
              ].map(({ title, value, key }) => (
                <div key={key} className="col-xl-3 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="border-bottom pb-3 mb-3">
                        <div className="row align-items-center">
                          <div className="col-7">
                            <div>
                              <span className="fs-14 fw-normal text-truncate mb-1">
                                {title}
                              </span>
                              <h5>{value}</h5>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex">
                        <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                          <span className="text-primary fs-12 d-flex align-items-center me-1">
                            <i className="ti ti-arrow-wave-right-up me-1" />
                            +19.01%
                          </span>
                          from last week
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <div className="icon-form mb-3 mb-sm-0">
                      <span className="form-icon">
                        <i className="ti ti-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          <i className="ti ti-package-export me-2" />
                          Export
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <ul>
                            <li>
                              <Link to="#" className="dropdown-item">
                                <i className="ti ti-file-type-pdf text-danger me-1" />
                                Export as PDF
                              </Link>
                            </li>
                            <li>
                              <Link to="#" className="dropdown-item">
                                <i className="ti ti-file-type-xls text-green me-1" />
                                Export as Excel
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="dropdown-toggle"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-sort-ascending-2 me-2" />
                        Sort
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul>
                          {[
                            { label: "Ascending", value: "asc" },
                            { label: "Descending", value: "desc" },
                            {
                              label: "Recently Viewed",
                              value: "recently_viewed",
                            },
                            {
                              label: "Recently Added",
                              value: "recently_added",
                            },
                          ].map(({ label, value }) => {
                            const isActive = sortOption === value;
                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${
                                    isActive ? "fw-bold text-primary" : ""
                                  }`}
                                  onClick={() => setSortOption(value)}
                                >
                                  <span>
                                    <i className="ti ti-circle-chevron-right me-1" />
                                    {label}
                                  </span>
                                  {isActive && <i className="ti ti-check" />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    <div className="icon-form">
                      <span className="form-icon">
                        <i className="ti ti-calendar" />
                      </span>
                      <DateRangePicker
                        initialSettings={initialSettings}
                        onCallback={(start, end) => {
                          setDateRange({
                            startDate: start.toDate(),
                            endDate: end.toDate(),
                          });
                        }}
                      >
                        <input
                          className="form-control bookingrange"
                          type="text"
                        />
                      </DateRangePicker>
                    </div>
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="btn bg-soft-purple text-purple"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-columns-3 me-2" />
                        Manage Columns
                      </Link>
                      <div className="dropdown-menu dropdown-menu-md-end dropdown-md p-3">
                        <h4 className="mb-2 fw-semibold">
                          Want to manage datatables?
                        </h4>
                        <p className="mb-3">
                          Please drag and drop your column to reorder your table
                          and enable see option as you want.
                        </p>
                        <div className="border-top pt-3">
                          {[
                            "Subscriber",
                            "Plan",
                            "Payment Method",
                            "Amount",
                            "Created Date",
                            "Expired On",
                            "Status",
                            "Actions",
                          ].map((column) => (
                            <div
                              key={column}
                              className="d-flex align-items-center justify-content-between mb-3"
                            >
                              <p className="mb-0 d-flex align-items-center">
                                <i className="ti ti-grip-vertical me-2" />
                                {column}
                              </p>
                              <div className="status-toggle">
                                <input
                                  type="checkbox"
                                  id={`col-${column
                                    .toLowerCase()
                                    .replace(" ", "-")}`}
                                  className="check"
                                  checked={visibleColumns.includes(column)}
                                  onChange={() => handleToggleColumn(column)}
                                />
                                <label
                                  htmlFor={`col-${column
                                    .toLowerCase()
                                    .replace(" ", "-")}`}
                                  className="checktoggle"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="form-sorts dropdown">
                      <Link
                        to="#"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-filter-share" />
                        Filter
                      </Link>
                      <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                        <div className="filter-set-view">
                          <div className="filter-set-head">
                            <h4>
                              <i className="ti ti-filter-share" />
                              Filter
                            </h4>
                          </div>
                          <div className="accordion" id="accordionExample">
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to="#"
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#Status"
                                  aria-expanded="false"
                                  aria-controls="Status"
                                >
                                  Status
                                </Link>
                              </div>
                              <div
                                className="filter-set-contents accordion-collapse collapse"
                                id="Status"
                                data-bs-parent="#accordionExample"
                              >
                                <div className="filter-content-list">
                                  <ul>
                                    {["Paid", "Unpaid"].map((status) => (
                                      <li key={status}>
                                        <div className="filter-checks">
                                          <label className="checkboxs">
                                            <input
                                              type="checkbox"
                                              defaultChecked={status === "Paid"}
                                            />
                                            <span className="checkmarks" />
                                            {status}
                                          </label>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to="#"
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#collapseOne"
                                  aria-expanded="false"
                                  aria-controls="collapseOne"
                                >
                                  Payment
                                </Link>
                              </div>
                              <div
                                className="filter-set-contents accordion-collapse collapse"
                                id="collapseOne"
                                data-bs-parent="#accordionExample"
                              >
                                <div className="filter-content-list">
                                  <ul>
                                    {[
                                      "Credit Card",
                                      "Paypal",
                                      "Debit Card",
                                    ].map((payment) => (
                                      <li key={payment}>
                                        <div className="filter-checks">
                                          <label className="checkboxs">
                                            <input
                                              type="checkbox"
                                              defaultChecked={
                                                payment === "Credit Card"
                                              }
                                            />
                                            <span className="checkmarks" />
                                            {payment}
                                          </label>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to="#"
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#collapseThree"
                                  aria-expanded="false"
                                  aria-controls="collapseThree"
                                >
                                  Plan
                                </Link>
                              </div>
                              <div
                                className="filter-set-contents accordion-collapse collapse"
                                id="collapseThree"
                                data-bs-parent="#accordionExample"
                              >
                                <div className="filter-content-list">
                                  <ul>
                                    {["Enterprise", "Advanced", "Basic"].map(
                                      (plan) => (
                                        <li key={plan}>
                                          <div className="filter-checks">
                                            <label className="checkboxs">
                                              <input
                                                type="checkbox"
                                                defaultChecked={
                                                  plan === "Enterprise"
                                                }
                                              />
                                              <span className="checkmarks" />
                                              {plan}
                                            </label>
                                          </div>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="filter-reset-btns">
                            <div className="row">
                              <div className="col-6">
                                <Link to="#" className="btn btn-light">
                                  Reset
                                </Link>
                              </div>
                              <div className="col-6">
                                <Link to="#" className="btn btn-primary">
                                  Filter
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="table-responsive custom-table">
                  <Table
                    dataSource={filteredData}
                    columns={columns}
                    loading={loading}
                    className="custom-table"
                    rowKey="subscription_id"
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"],
                      onChange: (page: number, size: number) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      },
                    }}
                    scroll={{ x: "max-content" }} // Enables horizontal scroll
                    components={{
                      header: {
                        cell: (props: any) => (
                          <th
                            {...props}
                            style={{
                              ...props.style,
                              position: "sticky",
                              top: 0,
                              zIndex: 2, // Better layering over scrollbars/fixed columns
                              background: "#fff",
                              boxShadow: "inset 0 -1px 0 #f50e0e", // ✅ fix this invalid color
                            }}
                          />
                        ),
                      },
                    }}
                  />
                </div>

                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="datatable-length" />
                  </div>
                  <div className="col-md-6">
                    <div className="datatable-paginate" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade" id="view_invoice">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-body p-5">
              <div className="row justify-content-between align-items-center mb-3">
                <div className="col-md-6">
                  <div className="mb-4">
                    <ImageWithBasePath
                      src="assets/img/logo.png"
                      className="img-fluid"
                      alt="logo"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="text-end mb-3">
                    <h5 className="text-dark mb-1">Invoice</h5>
                    <p className="mb-1 fw-normal">
                      <i className="ti ti-file-invoice me-1" />
                      INV0287
                    </p>
                    <p className="mb-1 fw-normal">
                      <i className="ti ti-calendar me-1" />
                      Issue date : 12 Sep 2024
                    </p>
                    <p className="fw-normal">
                      <i className="ti ti-calendar me-1" />
                      Due date : 12 Oct 2024
                    </p>
                  </div>
                </div>
              </div>
              <div className="row mb-3 d-flex justify-content-between">
                <div className="col-md-7">
                  <p className="text-dark mb-2 fw-medium fs-16">
                    Invoice From :
                  </p>
                  <div>
                    <p className="mb-1">Crms</p>
                    <p className="mb-1">797 Hilltop Street, Springfield</p>
                    <p className="mb-1">crms@example.com</p>
                  </div>
                </div>
                <div className="col-md-5">
                  <p className="text-dark mb-2 fw-medium fs-16">Invoice To :</p>
                  <div>
                    <p className="mb-1">BrightWave Innovations</p>
                    <p className="mb-1">
                      367 Hillcrest Lane, Irvine, California, United States
                    </p>
                    <p className="mb-1">michael@example.com</p>
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <div className="table-responsive mb-3">
                  <table className="table">
                    <thead className="thead-light">
                      <tr>
                        <th>Plan</th>
                        <th>Payment Date</th>
                        <th>Expiring On</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Advanced (Monthly)</td>
                        <td>12 Sep 2024</td>
                        <td>12 Oct 2024</td>
                        <td>$200</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="row mb-3 d-flex justify-content-between">
                <div className="col-md-4">
                  <div>
                    <h6 className="mb-4">Payment info:</h6>
                    <p className="mb-0">Credit Card - 123***********789</p>
                    <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                      <p className="mb-0">Amount</p>
                      <p className="text-dark fw-medium mb-2">$200.00</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex justify-content-between align-items-center pe-3">
                    <p className="text-dark fw-medium mb-0">Sub Total</p>
                    <p className="mb-2">$200.00</p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pe-3">
                    <p className="text-dark fw-medium mb-0">Tax</p>
                    <p className="mb-2">$0.00</p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pe-3">
                    <p className="text-dark fw-medium mb-0">Total</p>
                    <p className="text-dark fw-medium mb-2">$200.00</p>
                  </div>
                </div>
              </div>
              <div className="card border mb-0">
                <div className="card-body">
                  <p className="text-dark fw-medium mb-2">
                    Terms & Conditions:
                  </p>
                  <p className="fs-12 fw-normal d-flex align-items-baseline mb-2">
                    <i className="ti ti-point-filled text-primary me-1" />
                    All payments must be made according to the agreed schedule.
                    Late payments may incur additional fees.
                  </p>
                  <p className="fs-12 fw-normal d-flex align-items-baseline">
                    <i className="ti ti-point-filled text-primary me-1" />
                    We are not liable for any indirect, incidental, or
                    consequential damages, including loss of profits, revenue,
                    or data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade" id="delete_company" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remove Subscription?</h4>
                <p className="mb-0">
                  Are you sure you want to remove <br /> subscription you
                  selected?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link
                    to="#"
                    data-bs-dismiss="modal"
                    className="btn btn-danger"
                    onClick={() =>
                      handleDelete(subscriptions[0]?.subscription_id || "")
                    }
                  >
                    Yes, Delete it
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptionpayment;
